// Admin > Audit Log. Read-only paginated viewer with filters for actor,
// entity, action, date range. Click-through goes via lib/entity-routes.

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { auditLog } from '@/lib/api';
import { entityHref } from '@/lib/entity-routes';
import { useMe } from '@/hooks/useMe';
import type { AuditAction, AuditLogListItem, AuditLogListQuery } from '@frt/shared-types';

const ACTIONS: AuditAction[] = ['create', 'update', 'delete', 'suppress', 'restore'];

export function AdminAuditLogPage() {
  const { data: me } = useMe();
  const [filters, setFilters] = useState<AuditLogListQuery>({});
  const [page, setPage] = useState(1);

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin', 'audit-log', filters, page],
    queryFn: () => auditLog.list({ ...filters, page, per_page: 50 }),
    enabled: !!me && me.role === 'admin',
  });

  if (!me || me.role !== 'admin') {
    return <div className="text-sm text-slate-600">Audit log requires the admin role.</div>;
  }

  return (
    <div className="space-y-4">
      <h1 className="text-2xl font-semibold text-slate-900">Audit Log</h1>
      <Card>
        <CardHeader>
          <CardTitle>Filters</CardTitle>
        </CardHeader>
        <CardBody>
          <form
            className="grid grid-cols-2 md:grid-cols-4 gap-3"
            onSubmit={(e) => {
              e.preventDefault();
              setPage(1);
            }}
          >
            <label className="block text-sm">
              <span className="text-slate-600">Actor user ID</span>
              <Input
                type="number"
                value={filters.actor_user_id ?? ''}
                onChange={(e) =>
                  setFilters((f) => ({
                    ...f,
                    actor_user_id: e.target.value ? Number(e.target.value) : undefined,
                  }))
                }
              />
            </label>
            <label className="block text-sm">
              <span className="text-slate-600">Entity type</span>
              <Input
                value={filters.entity_type ?? ''}
                onChange={(e) =>
                  setFilters((f) => ({ ...f, entity_type: e.target.value || undefined }))
                }
                placeholder="user / account / …"
              />
            </label>
            <label className="block text-sm">
              <span className="text-slate-600">Action</span>
              <select
                value={filters.action ?? ''}
                onChange={(e) =>
                  setFilters((f) => ({
                    ...f,
                    action: (e.target.value || undefined) as AuditAction | undefined,
                  }))
                }
                className="block w-full rounded border border-slate-300 bg-white px-3 py-2 text-sm"
              >
                <option value="">all</option>
                {ACTIONS.map((a) => (
                  <option key={a} value={a}>
                    {a}
                  </option>
                ))}
              </select>
            </label>
            <label className="block text-sm">
              <span className="text-slate-600">Entity ID</span>
              <Input
                value={filters.entity_id ?? ''}
                onChange={(e) =>
                  setFilters((f) => ({ ...f, entity_id: e.target.value || undefined }))
                }
              />
            </label>
            <label className="block text-sm">
              <span className="text-slate-600">From (YYYY-MM-DD)</span>
              <Input
                type="date"
                value={filters.from ?? ''}
                onChange={(e) =>
                  setFilters((f) => ({ ...f, from: e.target.value || undefined }))
                }
              />
            </label>
            <label className="block text-sm">
              <span className="text-slate-600">To (YYYY-MM-DD)</span>
              <Input
                type="date"
                value={filters.to ?? ''}
                onChange={(e) =>
                  setFilters((f) => ({ ...f, to: e.target.value || undefined }))
                }
              />
            </label>
            <div className="col-span-2 md:col-span-4 flex gap-2">
              <Button type="submit">Apply</Button>
              <Button
                type="button"
                variant="ghost"
                onClick={() => {
                  setFilters({});
                  setPage(1);
                }}
              >
                Reset
              </Button>
            </div>
          </form>
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Events</CardTitle>
        </CardHeader>
        <CardBody className="space-y-3">
          {error && <div className="text-sm text-red-600">{(error as Error).message}</div>}
          {isLoading ? (
            <div className="text-sm text-slate-500">Loading…</div>
          ) : (
            <>
              <table className="w-full text-sm">
                <thead className="text-left text-slate-500">
                  <tr>
                    <th className="py-1 pr-3">When</th>
                    <th className="py-1 pr-3">Actor</th>
                    <th className="py-1 pr-3">Entity</th>
                    <th className="py-1 pr-3">Action</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {(data?.data ?? []).map((row) => (
                    <AuditRow key={row.id} row={row} />
                  ))}
                </tbody>
              </table>
              <Pagination
                page={data?.page ?? 1}
                perPage={data?.per_page ?? 50}
                total={data?.total ?? 0}
                onPage={setPage}
              />
            </>
          )}
        </CardBody>
      </Card>
    </div>
  );
}

function AuditRow({ row }: { row: AuditLogListItem }) {
  const [expanded, setExpanded] = useState(false);
  const href = entityHref(row.entity_type, row.entity_id);
  const diff = parseDiff(row.diff);
  return (
    <>
      <tr className="border-t border-slate-100">
        <td className="py-1 pr-3 text-slate-500 whitespace-nowrap">
          {new Date(row.at).toLocaleString()}
        </td>
        <td className="py-1 pr-3">
          {row.actor_username ?? <span className="text-slate-400">system</span>}
          <span className="ml-1 text-xs text-slate-400">#{row.actor_user_id ?? '—'}</span>
        </td>
        <td className="py-1 pr-3">
          <span className="text-slate-500">{row.entity_type}</span>
          <span className="mx-1 text-slate-300">/</span>
          {href ? (
            // entity routes are dynamic strings — bypass the typed router
            // surface with a cast.
            <Link
              to={href as never}
              className="font-mono text-brand-700 hover:underline"
            >
              {row.entity_id}
            </Link>
          ) : (
            <span className="font-mono">{row.entity_id}</span>
          )}
        </td>
        <td className="py-1 pr-3">
          <span
            className={
              row.action === 'delete' || row.action === 'suppress'
                ? 'text-red-600'
                : row.action === 'create'
                  ? 'text-emerald-700'
                  : 'text-slate-700'
            }
          >
            {row.action}
          </span>
        </td>
        <td className="py-1 text-right">
          {diff !== null && diff !== undefined ? (
            <Button size="sm" variant="ghost" onClick={() => setExpanded((v) => !v)}>
              {expanded ? 'Hide' : 'Diff'}
            </Button>
          ) : null}
        </td>
      </tr>
      {expanded && diff !== null && diff !== undefined && (
        <tr className="border-t border-slate-50">
          <td colSpan={5} className="bg-slate-50 px-3 py-2">
            <pre className="text-xs text-slate-700 overflow-x-auto whitespace-pre-wrap">
              {typeof diff === 'string' ? diff : JSON.stringify(diff, null, 2)}
            </pre>
          </td>
        </tr>
      )}
    </>
  );
}

function parseDiff(raw: string | null): unknown {
  if (!raw) return null;
  try {
    return JSON.parse(raw) as unknown;
  } catch {
    return raw;
  }
}

function Pagination({
  page,
  perPage,
  total,
  onPage,
}: {
  page: number;
  perPage: number;
  total: number;
  onPage: (p: number) => void;
}) {
  const lastPage = Math.max(1, Math.ceil(total / perPage));
  return (
    <div className="flex items-center justify-between text-xs text-slate-500">
      <div>
        {total === 0 ? 'No matches' : `Showing ${(page - 1) * perPage + 1}–${Math.min(page * perPage, total)} of ${total}`}
      </div>
      <div className="flex gap-2">
        <Button size="sm" variant="ghost" disabled={page <= 1} onClick={() => onPage(page - 1)}>
          ← Prev
        </Button>
        <span className="self-center">
          Page {page} / {lastPage}
        </span>
        <Button
          size="sm"
          variant="ghost"
          disabled={page >= lastPage}
          onClick={() => onPage(page + 1)}
        >
          Next →
        </Button>
      </div>
    </div>
  );
}
